/**
 * Acquisition Channels data, sourced from the Owners Hub Stats API.
 *
 * One card per affiliate type (`aft`). Each card makes its OWN `data-set-history`
 * call(s) against `daily_affiliate_activity`, grouped on `aft` AND filtered to the
 * card's `aft` literal (`aggregate_on: ["aft"]`, `filter: { aft }`) — so the cards
 * fetch independently and one channel erroring degrades just that card to `N/A`, never
 * the whole grid. The backend rejects an object-form `aggregate_on` (it must be the
 * string array `["aft"]`), so the channel value goes in `filter`; the response is then
 * read by MATCHING the echoed `aggregate_on.aft` to the card (see `toBucketsFor`),
 * which is robust whether the server narrows to the one filtered group or echoes
 * several. A channel with no rows simply reads zero. The slice cache keys include the
 * `aft` filter, so the per-card calls never clobber one another.
 *
 * Two windows are fetched on the live "Today" range: the selected window (figures +
 * mini-trend) and a SEPARATE prior-period window (the "vs …" delta baseline) — kept
 * as separate calls so the live call stays minimal (see `useHomeStats`); every other
 * range fetches ONE extended window split client-side. RisingWave's grain is daily,
 * so Today/Yesterday render a DayBars comparison rather than an intra-day line. See
 * `stats-mapping.ts`.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchStatsDataSetHistory, statsHistoryKey, statsQueryKey } from '@/store/slices/ownerHubStats';

import { useShowInProgress } from '../../hooks/useShowInProgress';
import type { HomeChannel, HomePeriod } from '../../mock-data/types';
import {
  cohortLabels,
  HOME_CHANNEL_STATS,
  HOME_CHANNELS,
  HOME_DEFAULT_CHANNEL_IDS,
  homeWindow,
  intervalIncludesToday,
  shiftWindow,
} from './stats-mapping';
import { chartSeries, dayPair, deltaPct, liveBuckets, matchedBaseline, resolvePeriods, sumBuckets, toBucketsFor } from './stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from './useRefreshTick';

export type HomeChannelsResult = {
  channels: HomeChannel[];
  /** Shared x-axis tick labels for the mini-charts (from the bucket dates). */
  dates: string[];
  /** Shared per-sample hover headings; `[]` outside the hourly ranges. */
  pointLabels: string[];
};

const { dataSet, aggregateOn, fields, ftdValueChartScale } = HOME_CHANNEL_STATS;
/** The single dimension the cards group/filter on (`aft`); each card matches its value. */
const DIMENSION = aggregateOn[0];

/**
 * @param casinoInstanceId Active brand id; `undefined` keeps the cards loading.
 * @param interval The selected UI range (mirrors the Key Metrics selector).
 */
export const useHomeChannels = (casinoInstanceId: string | undefined, interval: HomePeriod): HomeChannelsResult => {
  const dispatch = useAppDispatch();
  // Affiliates alone by default; the plain "777" reveal shows (and fetches) every card, live on a flip.
  const revealAll = useShowInProgress();
  const shownChannels = React.useMemo(
    () => (revealAll ? HOME_CHANNELS : HOME_CHANNELS.filter((ch) => HOME_DEFAULT_CHANNEL_IDS.includes(ch.id))),
    [revealAll],
  );

  const live = interval === 'TODAY';
  // Refresh on the 5-min cadence for every range that includes today's live slice
  // (all but Yesterday), not just Today — see useHomeStats / intervalIncludesToday.
  const includesToday = intervalIncludesToday(interval);
  const tick = useRefreshTick(includesToday, LIVE_REFRESH_MS);
  // `tick` is intentional — see useHomeStats: it rolls the window over at UTC midnight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, tick]);
  const priorWindow = React.useMemo(() => shiftWindow(window, window.spanDays), [window]);
  // Today: just the current window (+ a separate prior call). Other ranges: ONE
  // extended window covering both periods (split client-side). See useHomeStats.
  const mainWindow = live
    ? { startDate: window.startDate, endDate: window.endDate }
    : { startDate: priorWindow.startDate, endDate: window.endDate };

  // Select the keyed maps once (not one selector per channel) so the per-channel
  // lookups below stay outside React's hook rules.
  const historyByKey = useAppSelector((s) => s.ownerHubStats.historyByKey);
  const historyLoadingByKey = useAppSelector((s) => s.ownerHubStats.historyLoadingByKey);
  const historyErrorByKey = useAppSelector((s) => s.ownerHubStats.historyErrorByKey);

  // Main call PER channel — grouped on `aft` AND filtered to the card's `aft`, so each
  // card fetches its own channel independently. Re-dispatched on the tick so the
  // today-including ranges refresh.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    shownChannels.forEach((ch) => {
      dispatch(
        fetchStatsDataSetHistory({
          casinoInstanceId,
          dataSet,
          startDate: mainWindow.startDate,
          endDate: mainWindow.endDate,
          intervalSlice: window.intervalSlice,
          aggregateOn: [...aggregateOn],
          filter: { aft: ch.aft },
        }),
      );
    });
  }, [dispatch, casinoInstanceId, shownChannels, mainWindow.startDate, mainWindow.endDate, window.intervalSlice, tick]);

  // Separate prior call per channel — Today only.
  React.useEffect(() => {
    if (!casinoInstanceId || !live) return;
    shownChannels.forEach((ch) => {
      dispatch(
        fetchStatsDataSetHistory({
          casinoInstanceId,
          dataSet,
          startDate: priorWindow.startDate,
          endDate: priorWindow.endDate,
          intervalSlice: window.intervalSlice,
          aggregateOn: [...aggregateOn],
          filter: { aft: ch.aft },
        }),
      );
    });
  }, [dispatch, casinoInstanceId, shownChannels, live, priorWindow.startDate, priorWindow.endDate, window.intervalSlice]);

  const hasBrand = Boolean(casinoInstanceId);
  const now = Date.now();
  const currentStartMs = Date.parse(window.startDate);

  // Per-channel cache key: identity includes the `aft` filter (via statsQueryKey), so
  // the cards' calls never share a slot, matching the slice reducer's keying.
  const keyFor = (start: string, end: string, aft: string): string =>
    statsHistoryKey(casinoInstanceId ?? '', dataSet, start, end, window.intervalSlice, statsQueryKey(aggregateOn, { aft }));

  const built = shownChannels.map((ch) => {
    const mainKey = casinoInstanceId ? keyFor(mainWindow.startDate, mainWindow.endDate, ch.aft) : undefined;
    const priorKey = casinoInstanceId && live ? keyFor(priorWindow.startDate, priorWindow.endDate, ch.aft) : undefined;
    const mainEntry = mainKey ? historyByKey[mainKey] : undefined;
    const mainLoadingFlag = mainKey ? !!historyLoadingByKey[mainKey] : false;
    const mainErr = mainKey ? historyErrorByKey[mainKey] : undefined;
    const priorEntry = priorKey ? historyByKey[priorKey] : undefined;

    // Read the group matching this card's `aft` from its (filtered) response — robust
    // whether the server narrows to the one filtered group or echoes several.
    const mainBuckets = liveBuckets(mainEntry ? toBucketsFor(mainEntry.data, DIMENSION, ch.aft) : [], now);
    const priorBuckets = priorEntry ? toBucketsFor(priorEntry.data, DIMENSION, ch.aft) : [];
    const { current, previous } = resolvePeriods(mainBuckets, priorBuckets, live, currentStartMs);
    const baseline = matchedBaseline(current, previous);

    const registrations = sumBuckets(current, fields.registrations);
    const ftds = sumBuckets(current, fields.ftds);

    const loading = !hasBrand || (mainEntry === undefined && (mainLoadingFlag || mainErr === undefined));
    const error = !loading && mainErr !== undefined && mainEntry === undefined;

    const channel: HomeChannel = {
      id: ch.id,
      name: ch.name,
      icon: ch.icon,
      registrations,
      ftds,
      delta: deltaPct(registrations, sumBuckets(baseline, fields.registrations)),
      chart: {
        registrations: chartSeries(current, fields.registrations, 1),
        ftds: chartSeries(current, fields.ftds, 1),
        ftdValue: chartSeries(current, fields.ftdValue, ftdValueChartScale),
      },
      days: {
        registrations: dayPair(current, previous, fields.registrations, 1),
        ftds: dayPair(current, previous, fields.ftds, 1),
        ftdValue: dayPair(current, previous, fields.ftdValue, ftdValueChartScale),
      },
      loading,
      error,
    };
    return { channel, dates: current.map((b) => b.date) };
  });

  // All channels share one window, so any card that loaded carries the axis dates.
  const sharedDates = built.find((b) => b.dates.length > 0)?.dates ?? [];
  const labels = cohortLabels(interval, sharedDates);

  return { channels: built.map((b) => b.channel), dates: labels.xLabels, pointLabels: labels.pointLabels };
};

export default useHomeChannels;
